'use client';
import React, { useState, useEffect, useRef } from 'react';
import { useLanguage } from '@/context/LanguageContext';
import { ChevronLeft, ChevronRight, Sparkles, Palette, Package, FileText, Rocket, Star } from 'lucide-react';

const STEP_ICONS = [Sparkles, Palette, Package, FileText, Rocket, Star];
const STEP_COLORS = [
  { bg: 'bg-primary/15', border: 'border-primary/30', text: 'text-primary', glow: 'shadow-primary/20' },
  { bg: 'bg-accent/15', border: 'border-accent/30', text: 'text-accent', glow: 'shadow-accent/20' },
  { bg: 'bg-mint/15', border: 'border-mint/30', text: 'text-mint', glow: 'shadow-mint/20' },
  { bg: 'bg-pink-400/15', border: 'border-pink-400/30', text: 'text-pink-400', glow: 'shadow-pink-400/20' },
  { bg: 'bg-orange-400/15', border: 'border-orange-400/30', text: 'text-orange-400', glow: 'shadow-orange-400/20' },
  { bg: 'bg-yellow-400/15', border: 'border-yellow-400/30', text: 'text-yellow-400', glow: 'shadow-yellow-400/20' },
];

export default function DreamTransformationCarousel() {
  const { t } = useLanguage();
  const dt = (t as any)?.dreamTransformation;
  const [activeStep, setActiveStep] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const steps = [
    {
      key: 'dream',
      label: dt?.step1Label ?? 'Dream',
      title: dt?.step1Title ?? 'You Have an Idea',
      desc: dt?.step1Desc ?? 'It starts with a feeling — something you want to build, create, or launch. You type your dream in plain words.',
      visual: dt?.step1Visual ?? 'Coffee Shop Startup',
      tag: dt?.step1Tag ?? 'Your Dream',
    },
    {
      key: 'brand',
      label: dt?.step2Label ?? 'Brand',
      title: dt?.step2Title ?? 'Your Brand Takes Shape',
      desc: dt?.step2Desc ?? 'AI generates your brand name, color palette, personality, and visual identity — ready to use.',
      visual: dt?.step2Visual ?? 'Brew & Bloom Coffee',
      tag: dt?.step2Tag ?? 'Brand Identity',
    },
    {
      key: 'products',
      label: dt?.step3Label ?? 'Products',
      title: dt?.step3Title ?? 'Products Are Defined',
      desc: dt?.step3Desc ?? 'Your product line, pricing strategy, and packaging concepts are mapped out with clear descriptions.',
      visual: dt?.step3Visual ?? 'Signature Blends · Merch · Gift Sets',
      tag: dt?.step3Tag ?? 'Product Line',
    },
    {
      key: 'content',
      label: dt?.step4Label ?? 'Content',
      title: dt?.step4Title ?? 'Content Is Created',
      desc: dt?.step4Desc ?? 'Instagram posts, reels, stories, and ad concepts are generated — a full content calendar ready to publish.',
      visual: dt?.step4Visual ?? '30-Day Content Calendar',
      tag: dt?.step4Tag ?? 'Social Content',
    },
    {
      key: 'launch',
      label: dt?.step5Label ?? 'Launch',
      title: dt?.step5Title ?? 'Your Launch Plan Is Ready',
      desc: dt?.step5Desc ?? 'A 90-day roadmap with milestones, launch checklist, and campaign strategy — everything to go live.',
      visual: dt?.step5Visual ?? '90-Day Launch Roadmap',
      tag: dt?.step5Tag ?? 'Launch Strategy',
    },
    {
      key: 'result',
      label: dt?.step6Label ?? 'Result',
      title: dt?.step6Title ?? 'Your Dream Is Real',
      desc: dt?.step6Desc ?? 'From a single idea to a complete brand, product line, content strategy, and launch plan — in one session.',
      visual: dt?.step6Visual ?? 'Open for Business 🎉',
      tag: dt?.step6Tag ?? 'Dream Achieved',
    },
  ];

  useEffect(() => {
    if (!isAutoPlaying) return;
    intervalRef.current = setInterval(() => {
      setActiveStep(prev => (prev + 1) % steps.length);
    }, 3200);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [isAutoPlaying, steps.length]);

  const goTo = (idx: number) => {
    setActiveStep(idx);
    setIsAutoPlaying(false);
  };

  const prev = () => { goTo((activeStep - 1 + steps.length) % steps.length); };
  const next = () => { goTo((activeStep + 1) % steps.length); };

  const active = steps[activeStep];
  const color = STEP_COLORS[activeStep];
  const ActiveIcon = STEP_ICONS[activeStep];

  return (
    <section className="py-20 px-6 lg:px-10 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/3 left-1/4 w-72 h-72 bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-accent/5 rounded-full blur-3xl" />
      </div>

      <div className="max-w-6xl mx-auto relative">
        {/* Header */}
        <div className="text-center mb-14">
          <span className="inline-block text-xs font-700 uppercase tracking-widest text-primary mb-3 px-3 py-1 rounded-full bg-primary/10 border border-primary/20">
            {dt?.sectionLabel ?? 'How It Works'}
          </span>
          <h2 className="text-3xl md:text-4xl font-800 text-foreground mb-4">
            {dt?.sectionTitle ?? 'From Dream to Reality'}{' '}
            <span className="gradient-text">{dt?.sectionTitleHighlight ?? 'Step by Step'}</span>
          </h2>
          <p className="text-base text-muted-foreground max-w-2xl mx-auto">
            {dt?.sectionSubtitle ?? 'Watch how one idea becomes a complete brand, product line, content strategy, and launch plan.'}
          </p>
        </div>

        {/* Step pills */}
        <div className="flex items-center justify-center gap-2 mb-10 flex-wrap">
          {steps.map((step, i) => {
            const c = STEP_COLORS[i];
            const isActive = i === activeStep;
            return (
              <React.Fragment key={step.key}>
                <button
                  onClick={() => goTo(i)}
                  className={`px-4 py-2 rounded-full text-sm font-600 transition-all duration-300 border ${
                    isActive
                      ? `${c.bg} ${c.border} ${c.text} shadow-lg ${c.glow}`
                      : 'bg-card/50 border-border text-muted-foreground hover:border-primary/30 hover:text-foreground'
                  }`}
                >
                  {step.label}
                </button>
                {i < steps.length - 1 && (
                  <span className="text-muted-foreground/40 text-xs hidden sm:block">→</span>
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* Main card */}
        <div className="relative">
          <div
            key={activeStep}
            className={`glass-card rounded-3xl border ${color.border} p-8 md:p-12 transition-all duration-500`}
            style={{ animation: 'fadeSlideIn 0.4s ease-out' }}
          >
            <div className="grid md:grid-cols-2 gap-10 items-center">
              {/* Left: content */}
              <div>
                <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full ${color.bg} border ${color.border} mb-5`}>
                  <ActiveIcon size={14} className={color.text} />
                  <span className={`text-xs font-700 uppercase tracking-wider ${color.text}`}>{active.tag}</span>
                </div>
                <h3 className="text-2xl md:text-3xl font-800 text-foreground mb-4 leading-tight">
                  {active.title}
                </h3>
                <p className="text-base text-muted-foreground leading-relaxed mb-6">
                  {active.desc}
                </p>
                {/* Progress dots */}
                <div className="flex items-center gap-2">
                  {steps.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => goTo(i)}
                      className={`rounded-full transition-all duration-300 ${
                        i === activeStep
                          ? `w-6 h-2 ${color.bg.replace('/15', '')} opacity-100`
                          : 'w-2 h-2 bg-border hover:bg-muted-foreground/40'
                      }`}
                    />
                  ))}
                </div>
              </div>

              {/* Right: visual mock card */}
              <div className="flex justify-center">
                <div className={`relative w-full max-w-xs rounded-2xl ${color.bg} border ${color.border} p-6 shadow-xl`}>
                  {/* Mock UI chrome */}
                  <div className="flex items-center gap-1.5 mb-4">
                    <div className="w-2.5 h-2.5 rounded-full bg-red-400/60" />
                    <div className="w-2.5 h-2.5 rounded-full bg-yellow-400/60" />
                    <div className="w-2.5 h-2.5 rounded-full bg-green-400/60" />
                    <div className="ml-auto">
                      <ActiveIcon size={16} className={`${color.text} opacity-70`} />
                    </div>
                  </div>
                  {/* Step number */}
                  <div className={`text-5xl font-900 ${color.text} opacity-10 absolute top-4 right-6 select-none`}>
                    {String(activeStep + 1).padStart(2, '0')}
                  </div>
                  {/* Content */}
                  <div className="space-y-3">
                    <div className={`h-2 rounded-full ${color.bg} border ${color.border} w-3/4`} />
                    <div className={`h-2 rounded-full ${color.bg} border ${color.border} w-full`} />
                    <div className={`h-2 rounded-full ${color.bg} border ${color.border} w-2/3`} />
                  </div>
                  <div className={`mt-5 rounded-xl ${color.bg} border ${color.border} p-3`}>
                    <p className={`text-sm font-700 ${color.text} text-center`}>{active.visual}</p>
                  </div>
                  {/* Decorative dots */}
                  <div className="absolute -bottom-2 -right-2 w-8 h-8 rounded-full border-2 border-dashed opacity-20" style={{ borderColor: 'currentColor' }} />
                </div>
              </div>
            </div>
          </div>

          {/* Nav arrows */}
          <button
            onClick={prev}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 w-9 h-9 rounded-full glass-card border border-border flex items-center justify-center text-muted-foreground hover:text-foreground hover:border-primary/40 transition-all duration-200 hidden md:flex"
          >
            <ChevronLeft size={16} />
          </button>
          <button
            onClick={next}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 w-9 h-9 rounded-full glass-card border border-border flex items-center justify-center text-muted-foreground hover:text-foreground hover:border-primary/40 transition-all duration-200 hidden md:flex"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeSlideIn {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </section>
  );
}
